import * as React from "react";
import { useParams } from "react-router-dom";

import { Entry } from "../models/entries";
import { api } from "../api";
import { User, UserJSON } from "../models/users";
import { Compo } from "../models/compos";
import { Comment, CommentJSON } from "../models/comments";
import {
  CommentComponent,
  CommentAnonState,
} from "../DesignSystem/CommentComponent";
import { LeaveAComment, commentReplyHandle } from "../Comments/LeaveAComment";
import { Vote, VoteJSON } from "../models/vote";
import { EntryFinalResults } from "../models/entryfinalresults";
import { useUser } from "../state";
import { Tag } from "../models/tags";
import { EntryReview } from "../../../src/apidefs";
import { EntryJudgeResultsWidget } from "./JudgeResultsWidget";
import { RLink } from "../DesignSystem/RLink";
import { Button } from "../DesignSystem/Button";
import { CompoCategoriesClient } from "../../../src/entity/CompoCategoriesClient";
import { TwoColumnView, Page } from "../DesignSystem/formatting/TwoColumnView";
import { EntryTitle } from "../DesignSystem/EntryTitle";
import { EntryDescription } from "../DesignSystem/EntryDescription";
import { TagAdder } from "./Tag/TagAdder";
import { TagDeleter } from "./Tag/TagDeleter";
import { EntryResultsWidget } from "./EntryResultsWidget";
import { ShieldCheckIcon } from "@heroicons/react/24/solid";
import { DateWithTooltip } from "../DesignSystem/DateWithTooltip";

const STUPID_REASONS = [
  "The people must have a sore throat.",
  "The people need to speak up.",
  "The people are uncharacteristically quiet today.",
  "The people seem to be preoccupied with Rocket League instead.",
];

function getStupid(): string {
  return STUPID_REASONS[Math.floor(Math.random() * STUPID_REASONS.length)];
}

export function EntryPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const userState = useUser();
  const user = userState.type === "logged-in" ? userState.user : undefined;
  const entryRequestToken = React.useRef(0);

  const [state, setState] = React.useState<{
    entry: Entry | undefined;
    judge_votes: VoteJSON[];
    compo: Compo | undefined;
    categories: CompoCategoriesClient[] | undefined;
    vote: Vote | undefined;
    has_favorited: boolean;
    favorite_count: number | undefined;
    comments: {
      comment: CommentJSON;
      user: UserJSON;
      likes: number;
      is_liked: boolean;
      author_banned: boolean;
    }[];
    is_author: boolean;
    entry_users: User[];
    tags: Tag[];
    hidden: boolean;
    reviews: EntryReview[];
    final_results: EntryFinalResults | undefined;
    secret_santa_recipient: UserJSON | undefined;
    secret_santa_recipient_external_user_name: string | undefined;
  }>({
    entry: undefined,
    compo: undefined,
    is_author: false,
    judge_votes: [],
    favorite_count: undefined,
    entry_users: [],
    tags: [],
    reviews: [],
    comments: [],
    hidden: false,
    categories: undefined,
    vote: undefined,
    has_favorited: false,
    final_results: undefined,
    secret_santa_recipient: undefined,
    secret_santa_recipient_external_user_name: undefined,
  });

  const loadEntry = React.useCallback(
    async (entryId: number): Promise<void> => {
      const requestToken = ++entryRequestToken.current;
      let result;

      try {
        result = await api.get<"/api/entry/:id">(`/api/entry/${entryId}`);
      } catch (err) {
        console.error("Failed to load entry", err);
        return;
      }

      if (requestToken !== entryRequestToken.current) {
        return;
      }

      if (api.isToast(result)) {
        return;
      }

      const {
        reviews,
        tags,
        favorite_count,
        has_favorited,
        final_results,
        entry,
        vote,
        entry_users,
        is_author,
        compo,
        comments,
        categories,
        judge_votes,
        secret_santa_recipient,
        secret_santa_recipient_external_user_name,
      } = result;

      if (requestToken !== entryRequestToken.current) {
        return;
      }

      setState({
        reviews,
        tags: tags.map((t) => new Tag(t)),
        favorite_count,
        has_favorited,
        judge_votes,
        final_results,
        hidden: entry.hidden,
        entry: new Entry(entry),
        vote: vote ? new Vote(vote) : undefined,
        entry_users: entry_users.map((u) => new User(u)),
        is_author,
        compo: new Compo(compo),
        comments,
        categories: categories.map((x) => new CompoCategoriesClient(x)),
        secret_santa_recipient,
        secret_santa_recipient_external_user_name,
      });
    },
    []
  );

  React.useEffect(() => {
    loadEntry(Number(id));
  }, [id, loadEntry]);

  // Easter egg: remove all "e"s from the page for entry 2513
  React.useEffect(() => {
    if (id !== "2513" && id !== "2509") return;

    // Targeted replacements to preserve readability before blanket e removal
    const replacements: [RegExp, string][] = [
      [
        /Audio Secret Santa the 21st/g,
        "ChipsCompo Unknown Audio Santa Activity",
      ],
      [/Entry Details/g, "Song Information"],
      [/See stats/g, "Stats"],
      [/See compo results/g, "Compo"],
      [/Results/g, "Stats"],
      [/Submitted/g, "Submittal"],
      [/Uniqueness/g, "Unusualty"],
      [/Overall/g, "Total"],
      [/Structure/g, "Arranging"],
      [/Fits Request/g, "Obviously"],
      [/Emotion/g, "Passion"],
      [/Replay/g, "Again and Again"],
      [/Sound Design/g, "Sounds"],
      [/No song selected/g, "No song"],
    ];

    const removeEs = () => {
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        null
      );
      const textNodes: Text[] = [];
      while (walker.nextNode()) {
        textNodes.push(walker.currentNode as Text);
      }
      for (const node of textNodes) {
        if (node.nodeValue) {
          let text = node.nodeValue;
          // First do targeted replacements
          for (const [pattern, replacement] of replacements) {
            text = text.replace(pattern, replacement);
          }
          // Then remove remaining e's
          text = text.replace(/e/gi, "");
          node.nodeValue = text;
        }
      }
    };

    // Run once after render and set up a mutation observer to catch new content
    const timeoutId = setTimeout(removeEs, 100);
    const observer = new MutationObserver(removeEs);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [id]);

  if (!state.entry || !state.compo) {
    return <div> Loading! </div>;
  }

  return (
    <Page>
      <div>
        <div className="px-2">
          <EntryTitle
            entry={state.entry}
            user={user}
            hidden={state.hidden}
            hasFavorited={state.has_favorited}
            showCollabButton={state.is_author}
            entryUsers={state.entry_users}
            compoReleased={state.compo.released}
          />
        </div>

        <TwoColumnView
          left={
            <div
              style={{
                padding: "0 8px",
              }}
            >
              <div>
                <EntryDescription
                  entry={state.entry}
                  secretSantaRecipient={state.secret_santa_recipient}
                  secretSantaRecipientExternalUserName={
                    state.secret_santa_recipient_external_user_name
                  }
                />

                {state.compo.state === "judging" &&
                  user &&
                  state.entry_users.filter((x) => x.id === user.id).length ===
                    0 &&
                  state.entry.is_judged && (
                    <RLink to={`/compo/${state.entry.compo_id}/judge`}>
                      Vote on this song!
                    </RLink>
                  )}

                {state.entry.type === "art" && (
                  <div>
                    <img src={state.entry.uploaded_url} />
                  </div>
                )}

                {state.reviews.length > 0 && (
                  <div style={{ paddingTop: "30px" }}>
                    <h2 className="mb-4 flex items-center gap-3 text-2xl font-semibold text-slate-800 dark:text-white">
                      <i
                        className="fa fa-star text-slate-400 dark:text-slate-500"
                        aria-hidden="true"
                      ></i>
                      Reviews
                      <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-sm font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                        {state.reviews.length}
                      </span>
                    </h2>
                    {state.reviews.map((review, i) => (
                      <CommentComponent
                        key={i + " " + state.entry!.id}
                        anonymous={"never"}
                        replyCB={(username) =>
                          commentReplyHandle.replyTo(username)
                        }
                        compo={state.compo!}
                        vote={
                          state.reviews[i].judge
                            ? state.judge_votes.find(
                                (x) => x.user_id === state.reviews[i].id
                              )
                            : undefined
                        }
                        comment_user={review}
                        comment_likes={state.reviews[i].review_favorites}
                        comment_is_liked={state.reviews[i].has_favorited}
                        review={review}
                      />
                    ))}
                  </div>
                )}

                <div style={{ paddingTop: "50px" }}>
                  <h2 className="mb-4 flex items-center gap-3 text-2xl font-semibold text-slate-800 dark:text-white">
                    <i
                      className="fa fa-comments text-slate-400 dark:text-slate-500"
                      aria-hidden="true"
                    ></i>
                    Peanut Gallery
                    <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-sm font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                      {state.comments.length}
                    </span>
                  </h2>

                  {state.comments.map((comment, i) => {
                    let anonymous: CommentAnonState = "never";

                    if (comment.comment.anonymous && state.compo!.released) {
                      anonymous = "was anon";
                    }

                    if (
                      comment.comment.anonymous &&
                      user &&
                      comment.comment.user_id === user.id &&
                      !state.compo!.released
                    ) {
                      anonymous = "anon but yours";
                    }

                    return (
                      <div key={state.entry!.id * 100 + i}>
                        <CommentComponent
                          anonymous={anonymous}
                          replyCB={(username) =>
                            commentReplyHandle.replyTo(username)
                          }
                          compo={state.compo!}
                          comment_user={new User(state.comments[i].user)}
                          comment_likes={state.comments[i].likes}
                          comment_is_liked={state.comments[i].is_liked}
                          comment={new Comment(comment.comment)}
                          author_banned={state.comments[i].author_banned}
                        />
                      </div>
                    );
                  })}

                  {state.compo && state.comments.length === 0 && (
                    <div className="pb-2 text-[#aaaaaa]">
                      {getStupid()} Why not leave the first comment?
                    </div>
                  )}

                  <div style={{ paddingTop: "40px" }}>
                    {state.compo && state.entry && (
                      <LeaveAComment
                        canPostAnonymously={
                          user &&
                          state.entry.confirmedUserIds.includes(user.id) &&
                          state.entry.anonymous &&
                          state.compo.state !== "over"
                            ? true
                            : false
                        }
                        entry={state.entry!}
                        onAddComment={(comment) => {
                          setState((old) => ({
                            ...old,
                            comments: [
                              ...old.comments,
                              {
                                comment: comment,
                                user: user!,
                                likes: 0,
                                is_liked: false,
                                author_banned: false,
                              },
                            ],
                          }));
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          }
          right={
          <div
            style={{
              padding: "0 8px",
            }}
          >
            <div className="space-y-5">
              <div className="rounded-2xl border border-slate-200 bg-white/90 px-6 py-5 shadow-sm backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800 dark:shadow-none">
                <h3 className="mb-4 text-2xl font-bold text-slate-800 dark:text-white">
                  Entry Details
                </h3>
                <div className="space-y-3">
                  <div className="flex items-baseline gap-3">
                    <span className="w-[80px] text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
                      Compo
                    </span>
                    <span className="text-sm font-medium text-slate-700 dark:text-white/80">
                      <RLink to={"/compo/" + state.compo.id}>
                        {state.compo.name}
                      </RLink>
                    </span>
                  </div>

                  <div className="flex items-baseline gap-3">
                    <span className="w-[80px] text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
                      Submitted
                    </span>
                    <span className="text-sm text-slate-700 dark:text-white/80">
                      <DateWithTooltip date={new Date(state.entry.created)} />
                    </span>
                  </div>

                  <div className="flex items-start gap-3 overflow-hidden">
                    <span className="w-[80px] shrink-0 pt-1 text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
                      Tags
                    </span>
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                      {state.tags.map(({ id, tag, tag_slug }) => (
                        <span
                          key={id}
                          className="group inline-flex max-w-full cursor-pointer items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-sm text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                        >
                          <RLink
                            to={`/tag/${tag_slug}`}
                            variant="unstyled"
                            className="max-w-[200px] truncate hover:text-violet-600 dark:hover:text-violet-400"
                          >
                            {tag}
                          </RLink>
                          {user && user.admin && (
                            <TagDeleter
                              tag_id={id}
                              onDeleteTag={(tag_id) =>
                                setState((prevState) => ({
                                  ...prevState,
                                  tags: prevState.tags.filter(
                                    (ele) => ele.id !== tag_id
                                  ),
                                }))
                              }
                            />
                          )}
                        </span>
                      ))}
                      <TagAdder
                        onAddTag={(tag) =>
                          setState((prevState) => {
                            const newTag = new Tag({
                              created: new Date(),
                              tag,
                              entry_id: state.entry!.id,
                              user_id: user!.id,
                              id: Date.now(),
                              hidden: false,
                            });

                            return {
                              ...prevState,
                              tags: prevState.tags.concat(newTag),
                            };
                          })
                        }
                        entry_id={state.entry.id}
                      />
                    </div>
                  </div>

                  {user && user.admin && (
                    <div className="flex gap-2 border-t border-slate-200 pt-4 dark:border-slate-700">
                      <Button
                        variant="admin"
                        icon={<ShieldCheckIcon className="h-4 w-4" />}
                        onClick={() => {
                          api.post(`/api/entry/${state.entry!.id}/hide`);
                          setState((prev) => ({
                            ...prev,
                            hidden: !prev.hidden,
                          }));
                        }}
                      >
                        {state.hidden ? "UnHide" : "Hide"}
                      </Button>
                      <Button
                        to={`/entry/${state.entry.id}/edit`}
                        variant="admin"
                        icon={<ShieldCheckIcon className="h-4 w-4" />}
                      >
                        Edit
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {state.categories &&
                state.final_results &&
                state.compo.released && (
                  <>
                    <EntryResultsWidget
                      user={user}
                      entry={state.entry}
                      category_names={
                        state.categories.filter(
                          (c) => c.type === state.entry!.type
                        )[0]
                      }
                      results={state.final_results}
                    />

                    {state.compo.judged && (
                      <EntryJudgeResultsWidget
                        user={user}
                        entry={state.entry}
                        judge_votes={state.judge_votes}
                        category_names={
                          state.categories.filter(
                            (c) => c.type === state.entry!.type
                          )[0]
                        }
                        results={state.final_results}
                      />
                    )}
                  </>
                )}
            </div>
          </div>
        }
        />
      </div>
    </Page>
  );
}
